import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  cn,
  isDefensiveControl,
  isEmailControl,
  isIdentityProviderControl,
  JobStatusPerJobType,
  queries,
  useAuthConfig,
} from "@theprelude/core";
import { Button, RefreshIcon } from "@theprelude/ds";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { tb } from "@/lib/tracking";

const hasPendingJobs = (jobs: JobStatusPerJobType[] | undefined) =>
  jobs?.some((job) => !job.successful && !job.error);

export function ExecutiveBanner() {
  const [fetchAttempted, setFetchAttempted] = useState(false);
  const queryClient = useQueryClient();

  const authConfig = useAuthConfig();
  const { data: controls } = useQuery({
    ...queries.iam.getAccount(authConfig),
    select: (account) => account.controls,
  });

  const hasConnectedControls = controls?.some(
    (c) =>
      isDefensiveControl(c.id) ||
      isEmailControl(c.id) ||
      isIdentityProviderControl(c.id),
  );

  const { data: jobStatuses } = useQuery({
    ...queries.jobs.getJobStatuses(authConfig),
    enabled: hasConnectedControls,
    refetchInterval: (query) => {
      if (query.state.data) {
        if (hasPendingJobs(query.state.data.UPDATE_SCM)) {
          setFetchAttempted(true);
          return 30_000;
        }
        if (fetchAttempted) {
          void queryClient.invalidateQueries({ queryKey: [authConfig, "scm"] });
        }
        return false;
      }
    },
  });

  const dataIsCollecting = hasPendingJobs(jobStatuses?.UPDATE_SCM);

  return (
    <div className="relative">
      <AnimatePresence initial={false}>
        {!hasConnectedControls && (
          <motion.div
            key="noConnectedControls"
            initial={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          >
            <NoConnectedControlsCard />
          </motion.div>
        )}
        {hasConnectedControls && dataIsCollecting && (
          <motion.div
            key="dataIsCollecting"
            initial={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          >
            <CollectingDataCard />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const CollectingDataCard = () => {
  return (
    <div className="flex h-14 w-full items-center justify-start gap-2 rounded-bl-[16px] bg-soft-steel px-4 text-white">
      <RefreshIcon className="animate-spin" />
      <span className="text-base">
        Data collection is active. This may take some time depending on the
        integration.
      </span>
    </div>
  );
};

const NoConnectedControlsCard = () => {
  return (
    <div className="flex h-[295px] w-full justify-between overflow-hidden rounded-bl-[32px] bg-soft-steel">
      <div className="flex w-full flex-col items-start justify-center px-12 text-white">
        <span className="font-[450] font-serif text-[54px] leading-10 lg:leading-[80px]">
          Connect an integration
        </span>
        <span className="text-base">
          Secure data collection will start automatically.
        </span>
        <Link to="/account/integrations" className="pt-4">
          <Button
            intent="secondary"
            className={cn(
              "bg-white text-halloween-black",
              tb("connect-integration"),
            )}
          >
            Connect
          </Button>
        </Link>
      </div>
      <img
        src="/images/holding-grass.png"
        className="h-full w-full object-cover"
      />
    </div>
  );
};
